import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

const APP_STORE_URL = "https://apps.apple.com/ae/app/credit-stone/id6761537265";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_capital-motion/artifacts/ripah7iv_Screen%20Shot%202026-04-26%20at%207.53.59%20PM.png";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);
  const txtY = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#A6A8AC]"
      data-testid="hero-section"
      style={{ marginTop: 0 }}
    >
      {/* Hero image as full-bleed background */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0"
      >
        <img
          src={HERO_IMG}
          alt="Credit Stone — phone resting on stone"
          className="w-full h-full object-cover object-[center_35%] sm:object-center"
          draggable="false"
        />

        {/* very subtle vignette to integrate with nav */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(166,168,172,0.45) 0%, rgba(166,168,172,0) 22%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.35) 100%)",
          }}
        />
      </motion.div>

      {/* Top corner labels */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.4, delay: 0.8, ease }}
        className="absolute top-16 sm:top-24 left-6 sm:left-10 lg:left-12 z-10"
        style={{ textShadow: "0 1px 12px rgba(0,0,0,0.35)" }}
      >
        <div
          className="t-mono text-[#F4F2EC]"
          style={{ fontSize: "12.5px", letterSpacing: "0.2em" }}
        >
          N°01 — Credit Stone Digital
        </div>
        <div
          className="t-mono text-[#F4F2EC]/80 mt-1.5"
          style={{ fontSize: "11.5px", letterSpacing: "0.2em" }}
        >
          A private financial ecosystem
        </div>
      </motion.div>

      {/* Center title — restrained */}
      <motion.div
        style={{ y: txtY }}
        className="absolute inset-0 flex flex-col items-center justify-end pb-32 sm:pb-36 lg:pb-40 z-10 px-6 text-center"
      >
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.8, delay: 1.4, ease }}
          className="t-display text-[#F4F2EC] text-3xl sm:text-5xl lg:text-6xl max-w-3xl px-2"
          style={{ textShadow: "0 2px 24px rgba(0,0,0,0.45)" }}
        >
          Your private financial ecosystem.
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.5, delay: 1.7, ease }}
          className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto px-2 sm:px-0"
        >
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center justify-center gap-2 t-mono px-5 py-3 rounded-full bg-[#0F1013] text-[#F4F2EC] hover:bg-black transition-colors w-full sm:w-auto"
            data-testid="hero-primary-cta"
          >
            App access
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>
          <button
            onClick={() => {
              const el = document.getElementById("waitlist");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="t-mono px-5 py-3 rounded-full bg-white/40 backdrop-blur-md text-[#0F1013] border border-white/40 hover:bg-white/60 transition-colors w-full sm:w-auto text-center"
            data-testid="hero-secondary-cta"
          >
            ↳ Speak to a Relationship Manager
          </button>
        </motion.div>
      </motion.div>

      {/* Bottom-right tiny meta */}
      <div className="hidden lg:block absolute bottom-6 right-12 z-10 t-mono text-[#1A1B1E]/55">
        Scroll to begin
      </div>
    </section>
  );
}
